import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import { Plus, Trash2, X, Pencil, Upload, Image as ImageIcon, Coins } from "lucide-react";

const empty = { name: "", company: "", email: "", phone: "", address: "", zip: "", city: "", country: "CH", notes: "" };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [logoPath, setLogoPath] = useState(null);
  const [logoBust, setLogoBust] = useState(0);
  const logoInputRef = useRef(null);

  const fetchRows = async () => {
    const { data } = await api.get("/customers");
    setRows(data);
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(empty);
    setLogoPath(null);
    setError("");
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || "",
      company: row.company || "",
      email: row.email || "",
      phone: row.phone || "",
      address: row.address || "",
      zip: row.zip || "",
      city: row.city || "",
      country: row.country || "CH",
      notes: row.notes || "",
    });
    setLogoPath(row.logo_path || null);
    setError("");
    setOpen(true);
  };

  const uploadLogo = async (file) => {
    if (!file || !editId) return;
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post(`/customers/${editId}/logo`, fd);
      setLogoPath(data.logo_path);
      setLogoBust(Date.now());
      fetchRows();
    } catch (err) { setError(formatApiError(err)); }
  };

  const removeLogo = async () => {
    if (!editId) return;
    if (!window.confirm("Logo wirklich entfernen?")) return;
    await api.delete(`/customers/${editId}/logo`);
    setLogoPath(null);
    fetchRows();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editId) {
        await api.put(`/customers/${editId}`, form);
      } else {
        await api.post("/customers", form);
      }
      setOpen(false);
      setEditId(null);
      setForm(empty);
      fetchRows();
    } catch (err) { setError(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Kunde wirklich löschen?")) return;
    await api.delete(`/customers/${id}`);
    fetchRows();
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Stammdaten</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Kunden</h1>
          </div>
          <button onClick={openCreate} data-testid="customer-add-button" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
            <Plus size={16} strokeWidth={1.8} /> Neuer Kunde
          </button>
        </div>

        <div className="card mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">E-Mail</th>
                <th className="text-left py-3 px-4">Telefon</th>
                <th className="text-left py-3 px-4">Ort</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-[var(--text-3)]">Keine Kunden erfasst.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`customer-row-${r.id}`}>
                  <td className="py-3 px-4 text-[var(--text)]">{r.name}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.email || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.phone || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.zip} {r.city}</td>
                  <td className="py-3 px-4 text-right">
                    <Link to={`/admin/customers/${r.id}/pricing`} data-testid={`customer-pricing-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3 inline-flex"><Coins size={16} strokeWidth={1.5} /></Link>
                    <button onClick={() => openEdit(r)} data-testid={`customer-edit-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button onClick={() => del(r.id)} data-testid={`customer-delete-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">{editId ? "Kunde bearbeiten" : "Neuer Kunde"}</h2>
              <button onClick={() => setOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm mb-4">{error}</div>}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="label-eyebrow block mb-2">Name *</label><input required data-testid="customer-form-name" className="input-base" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-2">Firma</label><input className="input-base" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">E-Mail</label><input type="email" className="input-base" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Telefon</label><input className="input-base" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-2">Adresse</label><input className="input-base" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">PLZ</label><input className="input-base" value={form.zip} onChange={(e) => setForm({...form, zip: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Ort</label><input className="input-base" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
              {editId && (
                <div className="col-span-2 border-t border-[var(--border)] pt-4 mt-2">
                  <label className="label-eyebrow block mb-2">Kunden-Logo (optional · für Etiketten)</label>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    data-testid="customer-logo-input"
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                  />
                  {logoPath ? (
                    <div className="flex items-center gap-4">
                      <AuthImage path={`${logoPath}?v=${logoBust}`} alt="Logo" testid="customer-logo-preview" className="h-16 w-auto bg-white p-2 rounded-sm border border-[var(--border)]" />
                      <div className="flex flex-col gap-2">
                        <button type="button" onClick={() => logoInputRef.current?.click()} className="btn-ghost text-xs uppercase tracking-wider px-3 py-2 rounded-sm flex items-center gap-2"><Upload size={14} /> Ersetzen</button>
                        <button type="button" onClick={removeLogo} data-testid="customer-logo-remove" className="text-xs uppercase tracking-wider text-red-700 hover:text-red-700 flex items-center gap-2"><Trash2 size={14} /> Entfernen</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="customer-logo-upload"
                      className="w-full border border-dashed border-[var(--border-strong)] hover:border-[#D4AF37]/50 rounded-sm py-6 text-sm text-[var(--text-2)] hover:text-[#D4AF37] flex items-center justify-center gap-2"
                    >
                      <ImageIcon size={16} strokeWidth={1.5} /> Logo hochladen (PNG/JPG/SVG, max 8 MB)
                    </button>
                  )}
                  <p className="text-xs text-[var(--text-3)] mt-2">Wird neben dem NIVARA-Logo auf den Paletten-Etiketten angezeigt (Co-Branding).</p>
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
                <button type="submit" data-testid="customer-form-submit" className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider">{editId ? "Speichern" : "Anlegen"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
